import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import heroTravel from "@/assets/hero-travel.jpg";
import heroEducation from "@/assets/hero-education.jpg";
import heroHealthcare from "@/assets/hero-healthcare.jpg";
import WaitlistForm from "./WaitlistForm";

type VerticalKey = "travel" | "education" | "healthcare";

const BG: Record<VerticalKey, string> = {
  travel: heroTravel,
  education: heroEducation,
  healthcare: heroHealthcare,
};

const nextKey = (k: VerticalKey): VerticalKey =>
  k === "travel" ? "education" : k === "education" ? "healthcare" : "travel";

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState<VerticalKey>("education");
  const [prev, setPrev] = useState<VerticalKey>("education");
  const [fading, setFading] = useState(false);

  const reduceMotion = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const intervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (reduceMotion) return;
    intervalRef.current = window.setInterval(() => {
      show(nextKey(active));
    }, 5000);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reduceMotion]);

  const show = (k: VerticalKey) => {
    if (k === active) return;
    setPrev(active);
    setFading(true);
    setTimeout(() => {
      setActive(k);
      setFading(false);
    }, 1500);
  };

  const navigateToVertical = (vertical: VerticalKey) => {
    navigate(`/${vertical}`);
  };

  const scrollToHowItWorks = () => {
    const section = document.getElementById("how-it-works");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative overflow-hidden min-h-[90vh] flex items-center">
      {/* LAYER 1: Cross-fading hero images */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-right bg-cover transition-opacity ease-out"
        style={{
          backgroundImage: `url(${BG[prev]})`,
          backgroundPosition: "right center",
          opacity: fading ? 1 : 0,
          transitionDuration: reduceMotion ? "0ms" : "1500ms",
          filter: "brightness(1.1) saturate(1.05)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-right bg-cover transition-opacity ease-out"
        style={{
          backgroundImage: `url(${BG[active]})`,
          backgroundPosition: "right center",
          opacity: fading ? 0 : 1,
          transitionDuration: reduceMotion ? "0ms" : "1500ms",
          filter: "brightness(1.1) saturate(1.05)",
        }}
      />

      {/* LAYER 2: Directional gradient overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to right, rgba(13,31,53,0.92) 0%, rgba(13,31,53,0.6) 45%, rgba(13,31,53,0.0) 100%)",
        }}
        aria-hidden="true"
      />

      {/* FOREGROUND CONTENT */}
      <div className="relative z-10 w-full pt-24 md:pt-32 pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-left md:max-w-6xl md:pl-12">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-semibold leading-[1.1] tracking-tight text-white fade-in-slow md:max-w-[60%]">
            Finance life's biggest
            <br />
            milestones your way
          </h1>

          <p
            className="mt-6 max-w-xl text-lg md:text-xl text-white/80 leading-relaxed font-body fade-in-slow"
            style={{ animationDelay: "0.1s" }}
          >
            From dream holidays to tuition fees and essential healthcare—turn
            large costs into simple monthly plans with every financing option in
            one trusted marketplace.
          </p>

          <WaitlistForm
            source="hero"
            variant="hero"
            placeholder="Enter your email for early access"
            buttonText="Get Early Access"
            onHowItWorks={scrollToHowItWorks}
            className="mt-10 md:mt-12 fade-in-slow"
          />

          {/* Vertical chips */}
          <div
            className="mt-8 flex items-center justify-start gap-2 fade-in-slow"
            style={{ animationDelay: "0.3s" }}
          >
            {(["travel", "education", "healthcare"] as VerticalKey[]).map(
              (k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => navigateToVertical(k)}
                  onMouseEnter={() => show(k)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-all duration-300 ${
                    active === k && !fading
                      ? "bg-white/15 text-white border-white/25"
                      : "bg-transparent text-white/60 border-white/10 hover:bg-white/5 hover:text-white/80 hover:border-white/20"
                  }`}
                >
                  {k[0].toUpperCase() + k.slice(1)}
                </button>
              )
            )}
          </div>

          {/* Trust indicators */}
          <div
            className="mt-6 flex items-center justify-start gap-4 text-sm text-white/50 fade-in-slow"
            style={{ animationDelay: "0.4s" }}
          >
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/80" />
              AED 2K–200K+
            </span>
            <span className="w-px h-4 bg-white/20" />
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/80" />
              Flexible Terms
            </span>
            <span className="w-px h-4 bg-white/20" />
            <span className="flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/80" />
              Bank-Grade Security
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
